import { useState, FormEvent, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { ticketAPI, categoryAPI, attachmentAPI } from "../services/api";
import { toast } from "sonner";
import {
  AlertCircle,
  FileText,
  Upload,
  X,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { motion } from "framer-motion";

export default function NewTicket() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    categoryId: "",
    priority: "MEDIUM",
    description: "",
    type: "INCIDENT",
    status: "OPEN",
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.list();
      if (response.success || Array.isArray(response)) {
        const catList = Array.isArray(response)
          ? response
          : response.data?.categories ||
            response.data?.items ||
            response.data ||
            [];
        setCategories(Array.isArray(catList) ? catList : []);
      } else {
        setCategories([]);
        toast.error("Failed to load categories. Please refresh the page.");
      }
    } catch (error) {
      setCategories([]);
      toast.error("Failed to load categories. Please try again.");
    }
  };

  const priorities = [
    { value: "LOW", label: "Low", color: "text-blue-400" },
    { value: "MEDIUM", label: "Medium", color: "text-yellow-400" },
    { value: "HIGH", label: "High", color: "text-orange-400" },
    { value: "CRITICAL", label: "Critical", color: "text-red-400" },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.categoryId) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Description is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const description = (formData.description || "").toLowerCase();
      const title = (formData.title || "").toLowerCase();
      let autoPriority = formData.priority;

      if (
        description.includes("down") ||
        description.includes("outage") ||
        description.includes("critical") ||
        description.includes("urgent") ||
        title.includes("down") ||
        title.includes("critical")
      ) {
        autoPriority = "CRITICAL";
      } else if (
        description.includes("error") ||
        description.includes("broken") ||
        description.includes("not working") ||
        description.includes("failed")
      ) {
        autoPriority = "HIGH";
      }

      if (autoPriority !== formData.priority) {
        toast.info("Priority Auto-Adjusted", {
          description: `System assigned ${autoPriority} priority based on issue description`,
        });
      }

      const response = await ticketAPI.create({
        title: formData.title,
        description: formData.description,
        categoryId: formData.categoryId,
        priority: autoPriority,
        type: formData.type,
      });

      if (response?.success === false) {
        throw new Error(response?.message || "Failed to create ticket");
      }

      const ticketData = response?.data || response || {};
      const ticketId =
        ticketData.id ||
        ticketData.ticket?.id ||
        ticketData.data?.id ||
        ticketData.ticketId;

      toast.success("Ticket Created Successfully", {
        description: ticketId
          ? `Ticket ${ticketId} created and submitted`
          : "Your ticket was created successfully",
      });

      if (ticketId && attachments.length > 0) {
        void Promise.all(
          attachments.map((file) => attachmentAPI.upload(ticketId, file)),
        )
          .then(() => {
            toast.success("Attachments uploaded");
          })
          .catch(() => {
            toast.error("Some attachments failed to upload");
          });
      }

      toast.info("Email Notification Sent", {
        description: "Confirmation email sent to your inbox",
      });

      setIsSubmitting(false);
      if (ticketId) {
        navigate(`/dashboard/ticket/${ticketId}`);
      } else {
        navigate("/dashboard/history");
      }
      return;
    } catch (error) {
      toast.error("Failed to create ticket", {
        description: "Please try again later",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 font-serif">
              Submit New Ticket
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Create a new incident, service request, or complaint. All fields
              marked with * are required.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Issue Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Brief description of the issue"
                  className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    Category *
                  </label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                    required
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-foreground">
                    Priority Level *
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                    required
                  >
                    {priorities.map((pri) => (
                      <option key={pri.value} value={pri.value}>
                        {pri.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">
                  Detailed Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Please provide a detailed description of the issue, including any error messages or steps to reproduce..."
                  rows={6}
                  className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-foreground">
                  Request Type *
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                  required
                >
                  <option value="INCIDENT">
                    Incident (Something is broken/not working)
                  </option>
                  <option value="REQUEST">
                    Service Request (New service/access needed)
                  </option>
                  <option value="COMPLAINT">
                    Complaint (Issue with service quality)
                  </option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Select the type that best describes your submission
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Attachments
                </label>
                <div className="border-2 border-dashed border-border/50 rounded-lg p-4 sm:p-6 hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-foreground font-medium mb-1">
                      Click to upload files
                    </span>
                    <span className="text-xs text-muted-foreground text-center">
                      Supported: Images, PDF, Word documents (Max 10MB each)
                    </span>
                  </label>
                </div>

                {attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/30"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-foreground truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="p-1 hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground">
                <p className="font-semibold mb-1">What happens next?</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>
                    You'll receive a unique ticket ID and email confirmation
                  </li>
                  <li>
                    A technician will be automatically assigned based on
                    category
                  </li>
                  <li>You'll get updates via email at each stage</li>
                  <li>Track progress anytime from your dashboard</li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="relative flex-1 sm:flex-none group cursor-pointer"
              >
                <div className="absolute inset-0 bg-primary/20 blur-lg group-hover:blur-xl transition-all" />
                <div className="relative bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-3.5 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Submit Ticket</span>
                    </>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                disabled={isSubmitting}
                className="flex-1 sm:flex-none px-8 py-3.5 border border-border/50 hover:bg-muted rounded-lg font-semibold text-foreground transition-all disabled:opacity-50 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
